import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/auth';
import { useUIStore } from '@/state/store';
import { saveCallSession } from '@/lib/callSession';

interface TransferOffer {
  callId: string;
  fromName: string;
  steleName: string;
  groupName: string;
}

/**
 * Welle 9: Weiterleitungs-Angebot beim Ziel-Berater. Nimmt er an, übernimmt er den
 * laufenden Call (gleiche callId, neuer Agent-Peer); der abgebende Berater fällt heraus.
 * Zusätzlich: kurze Rückmeldung beim Abgebenden, wenn das Ziel ablehnt.
 */
export function TransferOfferPrompt() {
  const [offer, setOffer] = useState<TransferOffer | null>(null);
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);
  const [err, setErr] = useState('');
  const [declinedToast, setDeclinedToast] = useState('');
  const setView = useUIStore((s) => s.setView);

  useEffect(() => {
    function onOffer(e: any) { setOffer(e.detail as TransferOffer); setErr(''); }
    function onCleared(e: any) {
      setOffer((cur) => (cur && e.detail?.callId === cur.callId ? null : cur));
    }
    function onDeclined(e: any) {
      setDeclinedToast('Die Weiterleitung wurde abgelehnt – der Call bleibt bei dir.');
      setTimeout(() => setDeclinedToast(''), 4000);
      // sicherheitshalber: falls ich der Abgebende war und versehentlich ein Offer sah
      if (e.detail?.callId) onCleared(e);
    }
    window.addEventListener('bamoa:transfer-offer', onOffer);
    window.addEventListener('bamoa:call-cleared', onCleared);
    window.addEventListener('bamoa:transfer-declined', onDeclined);
    return () => {
      window.removeEventListener('bamoa:transfer-offer', onOffer);
      window.removeEventListener('bamoa:call-cleared', onCleared);
      window.removeEventListener('bamoa:transfer-declined', onDeclined);
    };
  }, []);

  async function accept() {
    if (!offer) return;
    setBusy('accept'); setErr('');
    try {
      const r = await apiFetch(`/api/calls/${offer.callId}/transfer/accept`, { method: 'POST' });
      if (r.ok) {
        const data = await r.json();
        const incoming = { callId: data.callId, steleName: offer.steleName, tenantName: '', group: offer.groupName, strategy: 'transfer', waitingMs: 0 };
        (useUIStore.getState() as any).rtcToken = data.rtcToken;
        (useUIStore.getState() as any).activeCallId = data.callId;
        (window as any).__rtcToken = data.rtcToken;
        (window as any).__activeCallId = data.callId;
        (window as any).__incomingCall = incoming;
        saveCallSession({ callId: data.callId, rtcToken: data.rtcToken, incoming });
        setOffer(null);
        setView('incall');
      } else {
        const b = await r.json().catch(() => ({}));
        setErr(b?.message || 'Übernahme fehlgeschlagen.');
      }
    } catch { setErr('Verbindung fehlgeschlagen.'); }
    finally { setBusy(null); }
  }

  async function decline() {
    if (!offer) return;
    setBusy('decline');
    try { await apiFetch(`/api/calls/${offer.callId}/transfer/decline`, { method: 'POST' }); } catch { /* */ }
    setOffer(null); setBusy(null);
  }

  return (
    <>
      {declinedToast && <div style={toast}>{declinedToast}</div>}
      {offer && (
        <div style={overlay} role="dialog" aria-label="Weiterleitung">
          <div style={card}>
            <div style={{ fontSize: 12, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--muted, #8aa)', marginBottom: 6 }}>
              Call-Übergabe
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>{offer.fromName} leitet einen Call an dich weiter</div>
            <div style={{ fontSize: 13.5, color: 'var(--muted, #8aa)', marginBottom: 16 }}>
              {offer.steleName}{offer.groupName ? ` · ${offer.groupName}` : ''}
            </div>
            {err && <div style={{ color: '#d8785a', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={accept} disabled={busy !== null} style={acceptBtn}>{busy === 'accept' ? 'Übernehme…' : 'Annehmen'}</button>
              <button onClick={decline} disabled={busy !== null} style={declineBtn}>Ablehnen</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 75, display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
};
const card: React.CSSProperties = {
  width: 'min(440px, 92vw)', background: 'var(--bg-elev-1, #181818)', border: '1px solid var(--hairline, #2a2a2a)',
  borderRadius: 16, padding: '22px 24px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
};
const acceptBtn: React.CSSProperties = {
  flex: 1, padding: '11px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
  background: '#22C55E', color: '#04210f', fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
};
const declineBtn: React.CSSProperties = {
  padding: '11px 18px', borderRadius: 10, border: '1px solid var(--hairline, #333)', cursor: 'pointer',
  background: 'transparent', color: 'var(--fg, #eee)', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
};
const toast: React.CSSProperties = {
  position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 80,
  background: 'var(--bg-elev-2, #222)', border: '1px solid var(--hairline, #333)', color: 'var(--fg, #eee)',
  padding: '10px 18px', borderRadius: 10, fontSize: 13, boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
};
